// Polymorphic notes attachable to any entity. INTERNAL_ONLY.

import type { AuditedRow, SoftDeletable, SqliteBoolean, UUID } from './common';

// Parent entity types a note can attach to. Mirrors the SQL CHECK
// constraint; keep the two in sync.
export type NoteEntityType =
  | 'contact'
  | 'household'
  | 'service_record'
  | 'opportunity'
  | 'transaction'
  | 'rhythm_instance'
  | 'vendor';

export type NoteSource =
  | 'manual'
  | 'migration'
  | 'ai_extracted'
  | 'voice_transcribed'
  | 'email_summary';

// The same entity types as API path segments (plural, kebab). Notes live
// at `/api/{segment}/:id/notes` for every one of these — the six kinds a
// person can actually write on, so `vendor` is absent (it has no UI
// doorway, deliberately; see HOUSEHOLD_NOTE_KINDS in
// shared/db/household-notes.ts for the same six-value subset).
//
// This lives in shared/ rather than the web client because BOTH sides
// speak it: the client builds the URL from it, and the routes are
// mounted under exactly these segments in workers/api/src/index.ts.
export type NoteEntitySegment =
  | 'households'
  | 'contacts'
  | 'service-records'
  | 'opportunities'
  | 'transactions'
  | 'rhythm-instances';

/** The six entity types a person can write a note on from the UI —
 *  everything except `vendor`, which has no note doorway. Same set as
 *  HOUSEHOLD_NOTE_KINDS in shared/db/household-notes.ts. */
export type WritableNoteEntityType = Exclude<NoteEntityType, 'vendor'>;

/** Segment ⇄ `notes.entity_type`. The one place the two note
 *  vocabularies meet — convert here rather than hand-rolling a map.
 *  Typed to the writable six, not all seven, so a caller that needs a
 *  vendor-free kind gets one without re-narrowing at every use site. */
export const NOTE_ENTITY_TYPE_BY_SEGMENT: Record<NoteEntitySegment, WritableNoteEntityType> = {
  households: 'household',
  contacts: 'contact',
  'service-records': 'service_record',
  opportunities: 'opportunity',
  transactions: 'transaction',
  'rhythm-instances': 'rhythm_instance',
};

export const NOTE_SEGMENT_BY_ENTITY_TYPE: Record<WritableNoteEntityType, NoteEntitySegment> = {
  household: 'households',
  contact: 'contacts',
  service_record: 'service-records',
  opportunity: 'opportunities',
  transaction: 'transactions',
  rhythm_instance: 'rhythm-instances',
};

export interface NoteRow extends AuditedRow, SoftDeletable {
  entity_type: NoteEntityType;
  entity_id: string; // not UUID-branded because the FK target varies

  author_user_id: UUID | null;

  // INTERNAL_ONLY.
  body: string;
  title: string | null;

  pinned: SqliteBoolean;
  important: SqliteBoolean;

  source: NoteSource;
}
